import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Route as RouteIcon, Play, Settings2, Wand2, Pencil } from "lucide-react";
import { api, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/ModulePage";
import { InspectionHistory } from "../components/InspectionHistory";
import { RagBadge } from "../components/Badges";
import { Button } from "../components/ui/button";

export default function Audits() {
  const { canWrite, isManager } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/pathways").then((r) => setRows(r.data));
  }, []);
  return (
    <div>
      <PageHeader eyebrow="Walkaround" title="Pathway Audits" description="Walk a defined route area by area (A to B to C). Each area lists its assets in route order with the right questions, then general area checks.">
        {isManager && <Button onClick={() => nav("/pathway-builder")} className="tap bg-rose-600 hover:bg-rose-700" data-testid="build-pathway-btn"><Wand2 className="mr-1 h-4 w-4" />Build a pathway</Button>}
        {isManager && <Button variant="outline" className="tap border-slate-700 bg-transparent" onClick={() => nav("/site-setup")} data-testid="manage-pathways-btn"><Settings2 className="mr-1 h-4 w-4" />Manage areas & pathways</Button>}
      </PageHeader>
      <div className="mb-8 grid gap-3 md:grid-cols-2">
        {rows.map((p) => (
          <div key={p.id} className="panel p-5" data-testid={`pathway-card-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-display text-xl font-bold uppercase">{p.name}</p>
                <p className="eyebrow text-[10px]">Every {p.frequency_days} day(s) - last run {p.last_run ? fmtDate(p.last_run) : "never"}</p>
              </div>
              <div className="flex items-center gap-1">
                {isManager && <Button size="icon" variant="ghost" onClick={() => nav(`/pathway-builder/${p.id}`)} data-testid={`edit-pathway-builder-${p.id}`}><Pencil className="h-4 w-4" /></Button>}
                <RagBadge status={p.rag} label={p.rag === "red" ? "Due / overdue" : p.rag === "amber" ? "Due today" : "Done"} />
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-1 text-xs">
              {p.area_names.map((n, i) => (
                <span key={i} className="flex items-center gap-1">
                  <span className="rounded bg-slate-800 px-2 py-1"><span className="font-mono text-rose-300">{String.fromCharCode(65 + (i % 26))}</span> {n}</span>
                  {i < p.area_names.length - 1 && <span className="text-slate-600">→</span>}
                </span>
              ))}
              {!p.area_names.length && <span className="text-slate-500">No areas on this route yet.</span>}
            </div>
            {canWrite && p.steps.length > 0 && (
              <Button onClick={() => nav(`/pathway/${p.id}`)} className="tap mt-4 w-full bg-rose-600 hover:bg-rose-700" data-testid={`start-pathway-${p.id}`}><Play className="mr-1 h-4 w-4" />Start audit</Button>
            )}
          </div>
        ))}
        {!rows.length && (
          <div className="panel col-span-full p-8 text-center text-sm text-slate-400">
            <RouteIcon className="mx-auto mb-2 h-8 w-8 text-slate-600" />No pathways yet. {isManager && <Link to="/site-setup" className="text-rose-300 underline">Create areas and a pathway</Link>}
          </div>
        )}
      </div>
      <h2 className="font-display mb-3 text-2xl font-bold uppercase">Audit history</h2>
      <InspectionHistory module="audit" />
    </div>
  );
}
